<template>
  <TreePicker
    ref="treePickerRef"
    :options="lazyOptions"
    :value="value"
    searchable
    :placeholder="placeholder"
    :unassigned-value="unassignedValue"
    :multiple="multiple"
    :full-path="fullPath"
    v-bind="$attrs"
    :as-input="asInput"
    :only-leaf-node-selectable="isOnlyLeafNodeSelectable"
    @blur="$emit('blur')"
    @change="$emit('change', $event)"
    @selected="$emit('selected', $event)"
    @show="hasOpened = true"
  >
    <template v-slot:trigger="slotData">
      <slot name="trigger" v-bind="slotData">
        <MTooltip v-if="suggestedItem && !value && !asInput">
          <template v-slot:trigger>
            <MTag
              class="flex items-center"
              :variant="undefined"
              :closable="false"
              :style="{
                ...currentStyle,
              }"
            >
              <MIcon name="lightbulb-exclamation" />
              <span class="blink_me mx-2">
                {{ suggestedItem.name }}
              </span>
            </MTag>
          </template>
          {{ $t('suggested_value') }}: {{ suggestedItem.name }}
        </MTooltip>
      </slot>
    </template>
    <template v-if="suggestedItem && !value" v-slot:before-menu>
      <div class="suggested-item-container" @click="handleSuggestedItemSelect">
        <MTooltip placement="topLeft">
          <template v-slot:trigger>
            <div class="text-ellipsis">
              <div class="font-bold">
                {{ $t('suggested_value') }}
              </div>
              {{ suggestedItem.name }}
            </div>
          </template>
          {{ suggestedItem.name }}
        </MTooltip>
      </div>
    </template>
  </TreePicker>
</template>

<script>
import SortBy from 'lodash/sortBy'
import { PreferenceComputed } from '@state/modules/preference'
import { getTagStyleFromColor } from '@utils/color'
import { flattenRecursive, buildHierarchy } from '@data/recursive'
import { CategoryComputed } from '@state/modules/category'
import { searchCategoriesApi } from '../../modules/category/category-api'
import { buildRelationalQualificationStructure } from '../../data/qualification'
import TreePicker from '@components/tree-picker/tree-picker'

export default {
  name: 'FlotoCategoryPicker',
  components: { TreePicker },
  model: { event: 'change' },
  props: {
    moduleName: { type: String, default: undefined, required: true },
    validateArchivedValue: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    placeholder: {
      type: String,
      default() {
        return this.$tc('select')
      },
    },
    unassignedValue: { type: [String, Number], default: 0 },
    value: { type: [Number, Array, String], default: undefined },
    suggestedValue: { type: [Number, Array, String], default: undefined },
    // eslint-disable-next-line
    asInput: { type: Boolean, default: true },
    // eslint-disable-next-line
    onlyLeafNodeSelectable: { type: Boolean, default: true },
    fullPath: { type: Boolean },
    ignoreHeirarchyConfig: { type: Boolean },
  },
  data() {
    return {
      archivedOptions: [],
      hasOpened: false,
    }
  },
  computed: {
    ...CategoryComputed,
    ...PreferenceComputed,
    transformedModuleName() {
      if (this.moduleName === this.$constants.SERVICE_CATALOG) {
        return this.$constants.REQUEST
      }
      return this.moduleName
    },
    isOnlyLeafNodeSelectable() {
      if (this.onlyLeafNodeSelectable) {
        if (this.ignoreHeirarchyConfig) {
          return this.onlyLeafNodeSelectable
        }
        return (
          this[`${this.transformedModuleName}OnlyLeafNodeSelectable`] || false
        )
      }
      return false
    },
    lazyOptions() {
      // Before opening: return minimal (selected items only - FAST)
      // After opening: return full tree (SLOW but only once)
      const baseOptions = this.hasOpened
        ? this.fullOptions
        : this.minimalOptions

      if (this.$attrs['additional-options']) {
        return (this.$attrs['additional-options'] || []).concat(baseOptions)
      }
      return baseOptions
    },
    minimalOptions() {
      // Lightweight: just selected items for display
      if (!this.value) return this.archivedOptions

      const rawCategories = this[`${this.transformedModuleName}Categories`]
      const flatOptions = flattenRecursive(rawCategories)
      const valueArray = Array.isArray(this.value) ? this.value : [this.value]
      const selected = flatOptions.filter((o) => valueArray.includes(o.id))

      return [...selected, ...this.archivedOptions]
    },
    fullOptions() {
      // Full expensive computation - buildHierarchy, sort, filter
      const options = this[`${this.transformedModuleName}Categories`]
      const flattenedOptions = flattenRecursive(options)
      const flatOptions = SortBy(
        flattenedOptions.filter((c) => !c.archived),
        'order'
      )
      const value = Array.isArray(this.value) ? this.value : [this.value]
      const selectedOptions = value.length
        ? flattenedOptions
            .filter((o) => value.indexOf(o.id) >= 0)
            .map((o) => ({ ...o, disabled: true }))
        : []
      let hierarchy = buildHierarchy(flatOptions)
      if (selectedOptions.length) {
        const availableIds = flatOptions.map((f) => f.id)
        hierarchy = hierarchy.concat(
          selectedOptions.filter((o) => availableIds.indexOf(o.id) === -1)
        )
      }
      return hierarchy.concat(this.archivedOptions)
    },
    suggestedItem() {
      if (this.suggestedValue && !this.value) {
        return this.lazyOptions.find((o) => o.id === this.suggestedValue)
      }
      return null
    },
    currentStyle() {
      if (this.suggestedItem) {
        return getTagStyleFromColor('#89c53b', this.theme)
      }
      return {}
    },
  },
  created() {
    // Check for missing categories using raw data (fast)
    if (this.value) {
      const v = Array.isArray(this.value) ? this.value : [this.value]
      const rawCategories = this[`${this.transformedModuleName}Categories`]
      const flattenedOptions = flattenRecursive(rawCategories)
      const allAvailableUserIds = flattenedOptions.map((u) => u.id)
      const missingCategories = v.filter(
        (userId) => allAvailableUserIds.indexOf(userId) === -1 && !isNaN(userId)
      )
      if (missingCategories.length) {
        if (this.validateArchivedValue && !this.multiple) {
          this.$emit('change', this.unassignedValue)
        } else {
          this.fetchMissingCategories(missingCategories)
        }
      }
    }
  },
  methods: {
    fetchMissingCategories(missingCategories) {
      searchCategoriesApi(
        this.moduleName,
        {
          quals: [
            buildRelationalQualificationStructure(
              'id',
              'in',
              missingCategories,
              'long',
              'db'
            ),
          ],
        },
        undefined,
        undefined,
        { archived: true }
      ).then((data) => {
        this.archivedOptions = Object.freeze(
          (data.items || []).map((c) => ({
            ...c,
            disabled: c.archived || c.disabled,
          }))
        )
      })
    },
    handleSuggestedItemSelect() {
      this.$emit('change', this.suggestedValue)
      this.$emit('blur')
      this.$refs.treePickerRef && this.$refs.treePickerRef.handleHide()
    },
  },
}
</script>
<style lang="less" scoped>
.suggested-item-container {
  @apply rounded mx-2 p-2 flex flex-col cursor-pointer;

  color: var(--secondary-green);
  background: var(--success-outline-button-hover-bg);
}
</style>
